import { useCallback } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUploadMutation } from "@/hooks/uploads/useUploadMutation";
import AgentSelection from "../AgentSelection";
import { UploadComponentProps } from "./types";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface UploadWebScrapingProps extends UploadComponentProps {
  scrapeUrl: string;
  onScrapeUrlChange: (url: string) => void;
}

const UploadWebScraping = ({
  availableAgents,
  selectedAgentId,
  onAgentSelect,
  scrapeUrl,
  onScrapeUrlChange,
  onUploadSuccess,
  onDeferredActionAdd,
}: UploadWebScrapingProps) => {
  const scrapeWebsiteMutation = useUploadMutation("scrape", selectedAgentId, onUploadSuccess);

  const handleSubmit = useCallback(() => {
    if (!scrapeUrl.trim()) {
      toast.error("Please enter a website URL");
      return;
    }

    if (onDeferredActionAdd) {
      onDeferredActionAdd({
        type: "web-scraping",
        data: {
          url: scrapeUrl,
          fileName: `Scraped: ${scrapeUrl}`,
        },
      });
      toast.success(`Website will be scraped after genie creation`);
      onScrapeUrlChange("");
    } else {
      scrapeWebsiteMutation.mutate({
        url: scrapeUrl,
        maxDepth: 1,
        maxPages: 10,
      });
    }
  }, [scrapeUrl, onScrapeUrlChange, onDeferredActionAdd, scrapeWebsiteMutation]);

  return (
    <>
      <AgentSelection
        agents={availableAgents}
        selectedAgentId={selectedAgentId}
        onAgentSelect={onAgentSelect}
      />
      <div className="flex flex-col items-center justify-center h-full">
        <GenieIcon name="globe" size={64} color="#4E9CFF" gradientEnabled className="mb-4" />
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Advanced Web Scraping</h3>
        <p className="text-sm text-gray-600 mb-6 text-center max-w-md">
          Crawl multiple pages from a website and extract all content automatically
        </p>
        <div className="flex flex-col sm:flex-row gap-3 w-full max-w-2xl">
          <Input
            type="url"
            placeholder="https://example.com"
            value={scrapeUrl}
            onChange={(e) => onScrapeUrlChange(e.target.value)}
            className="flex-1 text-base py-6"
            disabled={scrapeWebsiteMutation.isPending}
          />
          <Button
            className="bg-gradient-to-r from-genie-primary to-genie-secondary text-white font-semibold px-8 py-6 text-base shadow-lg hover:shadow-xl transition-all hover:scale-105 rounded-xl"
            onClick={handleSubmit}
            disabled={!scrapeUrl || scrapeWebsiteMutation.isPending}
          >
            {scrapeWebsiteMutation.isPending ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Scraping...
              </>
            ) : (
              "Start Scraping"
            )}
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-4 text-center max-w-md">
          📚 Automatically crawls up to 50 pages and extracts all content from the website
        </p>
      </div>
    </>
  );
};

export default UploadWebScraping;
